<template>
    <div class="sip-side-profile" :class="{ 'is-open': menuOpen }" v-click-outside="closeMenu">
        <button
            ref="profileBtn"
            type="button"
            class="sip-side-profile__btn"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            :aria-label="displayName"
            :title="isCollapsed ? displayName : null"
            @click.prevent="toggleMenu"
        >
            <span class="sip-avatar-wrap">
                <span class="sip-avatar" aria-hidden="true">{{ initials }}</span>
                <span class="sip-avatar-status" title="Online" aria-hidden="true"></span>
            </span>
            <span class="sip-side-profile__meta" v-if="!isCollapsed">
                <span class="sip-side-profile__name">{{ displayName }}</span>
                <span class="sip-side-profile__role-badge">{{ userRole }}</span>
            </span>
            <span class="sip-side-profile__action" v-if="!isCollapsed" title="Account Menu">
                <admin-icon name="chevron-down" :size="13" class="sip-side-profile__chev"></admin-icon>
            </span>
        </button>
        <div
            ref="profileMenu"
            class="sip-side-profile__menu"
            role="menu"
            :style="menuPos"
        >
            <div class="sip-side-profile__email" v-if="email">{{ email }}</div>
            <a v-if="hasRole('admin')" href="/subscription" role="menuitem">
                <admin-icon name="card" :size="16"></admin-icon>
                <span>{{ trans('subscription.subscription') }}</span>
            </a>
            <a href="/change/password" role="menuitem">
                <admin-icon name="lock" :size="16"></admin-icon>
                <span>{{ trans('user.change_password') }}</span>
            </a>
            <a href="#" role="menuitem" @click.prevent="logout" class="text-danger">
                <admin-icon name="logout" :size="16"></admin-icon>
                <span>{{ trans('auth.logout') }}</span>
            </a>
        </div>
    </div>
</template>

<script>
const MENU_GAP = 8;
const MENU_Z = 200;
const HEADER_GAP = 8;

export default {
    props: {
        isCollapsed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            menuOpen: false,
            menuPos: {},
        };
    },
    computed: {
        displayName() {
            return (this.getAuthUser('name') || '').trim() || 'Account';
        },
        email() {
            return (this.getAuthUser('email') || '').trim();
        },
        userRole() {
            if (this.hasRole('admin')) return 'Administrator';
            if (this.hasRole('principal')) return 'Principal';
            if (this.hasRole('teacher')) return 'Faculty';
            if (this.hasRole('staff')) return 'Staff';
            return 'School Admin';
        },
        initials() {
            const name = this.displayName;
            if (!name || name === 'Account') return 'A';
            const parts = name.split(/\s+/).filter(Boolean);
            const letters = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase());
            return letters.join('') || 'A';
        },
    },
    watch: {
        isCollapsed() {
            if (this.menuOpen) this.positionMenu();
        },
    },
    mounted() {
        this._onReposition = () => {
            if (this.menuOpen) this.positionMenu();
        };
    },
    beforeDestroy() {
        this.unbindMenuReposition();
    },
    methods: {
        toggleMenu() {
            if (this.menuOpen) {
                this.closeMenu();
                return;
            }
            this.positionMenu();
            this.menuOpen = true;
            this.bindMenuReposition();
        },
        closeMenu() {
            this.menuOpen = false;
            this.unbindMenuReposition();
        },
        fixedOrigin(el) {
            let node = el ? el.parentElement : null;
            while (node && node !== document.documentElement) {
                const style = window.getComputedStyle(node);
                const transformed = style.transform && style.transform !== 'none';
                const filtered = style.filter && style.filter !== 'none';
                const perspective = style.perspective && style.perspective !== 'none';
                if (transformed || filtered || perspective) {
                    const box = node.getBoundingClientRect();
                    return { left: box.left, top: box.top };
                }
                node = node.parentElement;
            }
            return { left: 0, top: 0 };
        },
        positionMenu() {
            const btn = this.$refs.profileBtn;
            const menu = this.$refs.profileMenu;
            if (!btn || !menu) return;

            const rect = btn.getBoundingClientRect();
            const origin = this.fixedOrigin(menu);
            const width = this.isCollapsed ? Math.max(rect.width, 210) : rect.width;
            const prevWidth = menu.style.width;
            menu.style.width = width + 'px';
            const height = menu.offsetHeight;
            menu.style.width = prevWidth;
            let top = rect.top - height - MENU_GAP;

            const header = document.querySelector('.nk-header.sip-admin-header, .nk-header-fixed');
            const minTop = header
                ? Math.max(HEADER_GAP, header.getBoundingClientRect().bottom + HEADER_GAP)
                : HEADER_GAP;
            if (top < minTop) top = minTop;

            let left = rect.left;
            const maxLeft = window.innerWidth - width - MENU_GAP;
            if (left > maxLeft) left = Math.max(MENU_GAP, maxLeft);
            if (left < MENU_GAP) left = MENU_GAP;

            this.menuPos = {
                position: 'fixed',
                top: (top - origin.top) + 'px',
                left: (left - origin.left) + 'px',
                width: width + 'px',
                right: 'auto',
                bottom: 'auto',
                zIndex: MENU_Z,
            };
        },
        bindMenuReposition() {
            this.unbindMenuReposition();
            window.addEventListener('resize', this._onReposition);
            window.addEventListener('scroll', this._onReposition, true);
        },
        unbindMenuReposition() {
            window.removeEventListener('resize', this._onReposition);
            window.removeEventListener('scroll', this._onReposition, true);
        },
        logout() {
            this.menuOpen = false;
            this.unbindMenuReposition();
            helper.logout().then(() => {
                this.$router.push('/login');
            });
        },
        getAuthUser(name) {
            return helper.getAuthUser(name);
        },
        hasRole(role) {
            return helper.hasRole(role);
        },
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el.__sipProfileOutside = (event) => {
                    if (!el.contains(event.target)) {
                        binding.value(event);
                    }
                };
                document.addEventListener('mousedown', el.__sipProfileOutside);
            },
            unbind(el) {
                document.removeEventListener('mousedown', el.__sipProfileOutside);
                delete el.__sipProfileOutside;
            },
        },
    },
};
</script>
